import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { uploadAdvertisementCreativeApi } from "../../../redux/api/api";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

// Recommended creative size per placement — derived from customer_app's
// components/misc/bannerLayout.ts (the app crops every image with `cover`
// into a fixed footprint). Sizes are ~3x the on-screen size so images stay
// sharp on high-density phones. Keep in sync if bannerLayout.ts changes.
export const CREATIVE_SPECS = {
  HOME_HERO: { width: 1230, height: 600, ratio: 2.05, label: "Hero slider" },
  HOME_OFFER: {
    width: 1000,
    height: 500,
    ratio: 2,
    label: "Offers carousel card",
  },
  HOME_OFFER_FOR_YOU: {
    width: 1000,
    height: 500,
    ratio: 2,
    label: "Recommended carousel card",
  },
  HOME_TOP_PROMO: {
    width: 1200,
    height: 350,
    ratio: 3.43,
    label: "Promo strip",
  },
  HOME_SERVICE_PROMO: {
    width: 1200,
    height: 350,
    ratio: 3.43,
    label: "Promo strip",
  },
  HOME_BETWEEN_SERVICES: {
    width: 1200,
    height: 350,
    ratio: 3.43,
    label: "Promo strip",
  },
};
// Ratio drift (±) tolerated before warning that the app will crop noticeably.
const RATIO_TOLERANCE = 0.12;

/** Spec for a placement, or null when the placement isn't chosen yet / has no app slot. */
export const getCreativeSpec = (placement) => CREATIVE_SPECS[placement] || null;

export const formatSpec = (spec) => `${spec.width} × ${spec.height}px`;

const SpecHint = ({ spec }) =>
  spec ? (
    <Typography variant="caption" color="text.secondary" display="block">
      Recommended {formatSpec(spec)} ({spec.label}, ~{spec.ratio}:1) · keep
      text/logos inside the centre 90%
    </Typography>
  ) : (
    <Typography variant="caption" color="text.secondary" display="block">
      Recommended: Hero 1230 × 600px · Offer/Recommended cards 1000 × 500px ·
      Promo strips 1200 × 350px · keep text/logos inside the centre 90%
    </Typography>
  );

const ratioWarning = (value, spec) => {
  if (!spec || !value?.width || !value?.height) return "";
  const ratio = value.width / value.height;
  if (Math.abs(ratio - spec.ratio) / spec.ratio > RATIO_TOLERANCE) {
    return `This image is ${ratio.toFixed(2)}:1 — the app shows this slot at ~${spec.ratio}:1, so edges will be cropped.`;
  }
  if (value.width < spec.width * 0.6)
    return `Low resolution (${value.width}px wide) — may look blurry on phones.`;
  return "";
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
};

/**
 * Professional R2-backed image uploader — browse or drag/drop, client-side
 * MIME/size/dimension validation before the network call, upload progress,
 * preview, replace, remove. The backend (Api/controllers/Admin/
 * advertisement.controller.js uploadCreativeImage) re-validates MIME/size
 * server-side regardless — this is a UX layer, not the security boundary.
 *
 * On success, calls onUploaded({ objectKey, imageUrl, thumbnailUrl, fileSize, mimeType, width, height }).
 * Call onRemove() to clear a previously-uploaded image (does not delete the
 * R2 object — see utils/r2Storage.js's comment on why deletion is manual).
 */
const ImageUploader = ({
  campaignId,
  language,
  assetType = "hero",
  placement,
  value,
  onUploaded,
  onRemove,
  label,
}) => {
  const spec = getCreativeSpec(placement);
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const readImageDimensions = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        resolve({ width: null, height: null });
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Banner must be JPG, PNG or WebP.");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError("Maximum file size is 5 MB.");
      return;
    }
    if (!campaignId) {
      setError("Save the campaign's basic info first, then upload the image.");
      return;
    }

    const dimensions = await readImageDimensions(file);

    const formData = new FormData();
    formData.append("image", file);
    formData.append("campaignId", campaignId);
    formData.append("language", language);
    formData.append("assetType", assetType);

    setUploading(true);
    setProgress(0);
    try {
      const response = await uploadAdvertisementCreativeApi(
        formData,
        setProgress,
      );
      const info = response?.data?.info || response?.data?.data;
      onUploaded({
        ...info,
        width: dimensions.width,
        height: dimensions.height,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to upload image. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    handleFile(file);
  };

  if (value?.imageUrl) {
    const warning = ratioWarning(value, spec);
    return (
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 1.5,
          position: "relative",
        }}
      >
        <Box
          component="img"
          src={value.imageUrl}
          alt={label || "Advertisement creative"}
          sx={{
            width: "100%",
            aspectRatio: `${spec?.ratio || 2}`,
            objectFit: "cover",
            borderRadius: 1,
            display: "block",
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {value.width && value.height ? (
              <Chip size="small" label={`${value.width} × ${value.height}px`} />
            ) : null}
            {value.fileSize ? (
              <Chip size="small" label={formatBytes(value.fileSize)} />
            ) : null}
          </Box>
          <Box>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => inputRef.current?.click()}
              sx={{ mr: 1 }}
            >
              Replace
            </Button>
            <IconButton size="small" color="error" onClick={onRemove}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ mt: 0.5 }}>
          <SpecHint spec={spec} />
          {warning ? (
            <Typography variant="caption" color="warning.main" display="block">
              {warning}
            </Typography>
          ) : null}
          {error ? (
            <Typography variant="caption" color="error" display="block">
              {error}
            </Typography>
          ) : null}
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          hidden
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragActive ? "primary.main" : "#c9c9d1",
          backgroundColor: dragActive ? "rgba(25,118,210,0.04)" : "#fafafa",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          cursor: uploading ? "default" : "pointer",
        }}
      >
        <CloudUploadIcon
          sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label || "Upload Advertisement"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          Drag & drop image here, or click to browse
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          JPG / PNG / WebP · Max 5 MB
        </Typography>
        <SpecHint spec={spec} />
        {uploading ? (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              Uploading… {progress}%
            </Typography>
          </Box>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          hidden
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </Box>
      {error ? (
        <Typography
          variant="caption"
          color="error"
          display="block"
          sx={{ mt: 0.5 }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ImageUploader;
